import { useDiagnosisReporting } from "../../../api/useReporting";
import { useTranslations, type Translation } from "../../../api/useTranslations";
import t from "../../../i18n";
import { translationLocale } from "../../../i18n/locale";
import React, { useMemo, useState } from "react";
import { colors, spacings } from "@ui/Theme";
import RepeatLine from "@ui/IconV2/react/RepeatLine";
import { css } from "@linaria/core";
import { QuestionMark } from "@ui/IconV2/react";
import { Tag } from "@ui/Tag";
import Ellipsis from "@ui/Ellipsis";

import {
  getFailureGroupName,
  calculateCost,
  getBorderColor,
  formatMonetaryValue,
  useReparabilityDescriptions,
  getReparabilityCategories,
  getLighterBackgroundColor,
  buildReparabilityMatrix,
  buildMatrixEntry,
  MATRIX_UI_CONSTANTS,
  type MatrixEntry,
  type ImpactRange,
  type MatrixReporting,
  type MatrixFilters,
  type ReparabilityKey,
} from "./faultMappingUtils";
import type { Reparability } from "../../../api/Reparability";

// ================================
// Utility Functions
// ================================

const getReparabilityCategory = (repairIndex: number): Reparability => {
  const categories = getReparabilityCategories();
  return categories[repairIndex] ?? "REPARABILITY_1"; // Default to ATF (REPARABILITY_1)
};

const getMatrixCellClasses = (
  hasData: boolean,
  isLastColumn: boolean,
  isLastRow: boolean,
): string =>
  [
    matrixCellBase,
    isLastColumn ? matrixCellLastColumn : matrixCellNotLastColumn,
    hasData ? matrixCellWithData : "",
    isLastColumn && isLastRow ? matrixCellBorderLastCorner : "",
  ]
    .filter(Boolean)
    .join(" ");

const formatImpactDisplay = (impact: number): string => {
  // Show "<1MW" for impacts below 1MW, otherwise show rounded integer
  if (Math.abs(impact) < 1) {
    return "(<1 MW)";
  } else {
    const roundedImpact = Math.round(impact);
    return `(${roundedImpact} MW)`;
  }
};

const formatPlantTooltip = (plantName: string, impact: number): string => {
  const isFrench = translationLocale === "fr";

  if (isFrench) {
    // French format: space before colon, comma for decimal separator
    const formattedImpact = impact.toFixed(1).replace(".", ",");
    return `${plantName} - Impact : ${formattedImpact} MW`;
  } else {
    // English format: no space before colon, dot for decimal separator
    const formattedImpact = impact.toFixed(1);
    return `${plantName} - Impact: ${formattedImpact} MW`;
  }
};

const processPlants = (plants: string[], entries: MatrixEntry[]) => {
  const plantData = plants.map(plant => {
    const plantEntries = entries.filter(entry => entry.trigram === plant);
    const totalPlantImpact = plantEntries.reduce((sum, entry) => sum + entry.impact, 0);
    const plantEntry = plantEntries[0];
    return {
      plant,
      plantEntry: plantEntry ? { ...plantEntry, impact: totalPlantImpact } : undefined,
      impact: totalPlantImpact,
    };
  });

  return plantData.sort((a, b) => b.impact - a.impact);
};

// Function to calculate total financial impact
const calculateTotalFinancialImpact = (matrixData: {
  matrix: { [reparabilityCategory: string]: { [impactRange: string]: MatrixEntry[] } };
  reparabilityCategories: Reparability[];
  impactRanges: ImpactRange[];
}): number => {
  let totalImpact = 0;
  let categoryImpact = 0;

  // Sum all impacts across all reparabilities
  for (let categoryIndex = 0; categoryIndex < 3; categoryIndex++) {
    categoryImpact = 0;
    const category = matrixData.reparabilityCategories[categoryIndex];
    if (!category) return 0;

    const reparabilityCategory = getReparabilityCategory(categoryIndex);

    // Sum all impacts across all ranges for this category
    for (const range of matrixData.impactRanges) {
      const cellData = matrixData.matrix[category]?.[range.label] || [];
      for (const entry of cellData) {
        categoryImpact += Math.abs(entry.impact);
      }
    }
    totalImpact += calculateCost(categoryImpact, reparabilityCategory);
  }

  return totalImpact;
};

// Function to calculate total financial impact per reparability category
const calculateColumnFinancialImpact = (
  matrixData: {
    matrix: { [reparabilityCategory: string]: { [impactRange: string]: MatrixEntry[] } };
    reparabilityCategories: Reparability[];
    impactRanges: ImpactRange[];
  },
  categoryIndex: number,
): number => {
  const category = matrixData.reparabilityCategories[categoryIndex];
  if (!category) return 0;

  const reparabilityCategory = getReparabilityCategory(categoryIndex);
  let totalImpact = 0;

  // Sum all impacts across all ranges for this category
  for (const range of matrixData.impactRanges) {
    const cellData = matrixData.matrix[category]?.[range.label] || [];
    for (const entry of cellData) {
      totalImpact += Math.abs(entry.impact);
    }
  }

  return calculateCost(totalImpact, reparabilityCategory);
};

// ================================
// Types and Interfaces
// ================================

interface DisplayItem {
  label: string;
  count: number;
  plants: string[];
  totalImpact: number;
  totalCost: number;
  entries: MatrixEntry[];
  hasRecurrentFaults: boolean;
}

const matrixContainer = css`
  display: flex;
  flex-direction: column;
`;

const totalFinancialImpactRow = css`
  display: flex;
  background-color: ${MATRIX_UI_CONSTANTS.HEADER_BACKGROUND};
  border-bottom: 2px solid ${colors.titanium200};
  padding: ${MATRIX_UI_CONSTANTS.HEADER_CELL_PADDING};
  justify-content: flex-end;
`;

const totalFinancialImpactValue = css`
  font-size: ${MATRIX_UI_CONSTANTS.FONT_SIZE_SMALL}px;
  font-weight: 600;
  color: ${colors.carbon900};
  text-align: right;
`;

const headerRow = css`
  display: flex;
`;

const headerSpacer = css`
  width: ${MATRIX_UI_CONSTANTS.Y_AXIS_WIDTH}px;
  background-color: ${MATRIX_UI_CONSTANTS.HEADER_BACKGROUND};
`;

const headerCell = css`
  flex: 1;
  margin-right: ${MATRIX_UI_CONSTANTS.MATRIX_GAP};
  padding: ${MATRIX_UI_CONSTANTS.HEADER_CELL_PADDING};
  font-size: ${MATRIX_UI_CONSTANTS.FONT_SIZE_SMALL}px;
  color: ${MATRIX_UI_CONSTANTS.HEADER_TEXT_COLOR};
  background-color: ${MATRIX_UI_CONSTANTS.HEADER_BACKGROUND};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-child {
    margin-right: 0;
  }

  &:not(:last-child) {
    border-top-right-radius: 0;
  }
`;

const headerCellTitle = css`
  font-weight: bold;
  text-align: left;
`;

const headerCellFinancialImpact = css`
  font-size: ${MATRIX_UI_CONSTANTS.FONT_SIZE_SMALL}px;
  font-weight: 600;
  color: ${colors.carbon900};
  text-align: right;
`;

const matrixRow = css`
  display: flex;
  align-items: stretch;
  margin-bottom: ${MATRIX_UI_CONSTANTS.MATRIX_GAP};

  &:last-child {
    margin-bottom: 0;
  }
`;

const yAxisLabel = css`
  width: ${MATRIX_UI_CONSTANTS.Y_AXIS_WIDTH}px;
  padding: ${MATRIX_UI_CONSTANTS.HEADER_CELL_PADDING};
  font-size: ${MATRIX_UI_CONSTANTS.FONT_SIZE_SMALL}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MATRIX_UI_CONSTANTS.HEADER_TEXT_COLOR};
  background-color: ${MATRIX_UI_CONSTANTS.HEADER_BACKGROUND};
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
`;

const failureList = css`
  margin: 0 0 0 0;
  padding: 0 0 0 0;
  clear: both;
`;

const failureItem = css`
  margin-bottom: ${spacings.xxs};
  overflow: hidden;
  min-width: 0;
`;

const failureHeader = css`
  display: flex;
  align-items: center;
  margin-bottom: ${spacings.xxs};
  margin-top: ${spacings.xxs};
  font-size: ${spacings.s};
  font-weight: bold;
  overflow: hidden;
  min-width: 0;
  padding-right: 70px;
`;

const failureLabel = css`
  margin-right: ${spacings.xs};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${colors.carbon800};
`;

const plantsContainer = css`
  font-size: 0.8em;
  color: #666;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.xs};
  margin-top: 2px;
`;

const emptyCellMessage = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${colors.carbon500};
  font-style: italic;
  font-size: 14px
  margin-top: 2 ${spacings.xxs};
  padding: 20px;
`;

const recurringLegendContainer = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${spacings.xxs};
  margin-top: -${spacings.xs};
  padding: ${spacings.xs} ${spacings.s};
  font-size: ${spacings.s};
  color: ${colors.carbon700};
`;

const recurringLegendLabel = css`
  margin-right: ${spacings.xs};
  font-weight: 500;
`;

const legendBadgeRecurrent = css`
  &.light {
    background-color: ${colors.phosphorus100};
  }
`;

const legendBadgeUnknownReparability = css`
  &.light {
    background-color: #fff3cd;
  }
`;

const legendBadgeCost = css`
  &.light {
    background-color: ${colors.titanium100};
  }
  font-size: ${spacings.s};
`;

const plantItem = css`
  display: inline-flex;
  align-items: center;
  margin-bottom: ${spacings.xxs};
`;

const impactCostText = css`
  margin-left: ${spacings.xxs};
  font-size: 1em;
`;

const matrixCellBase = css`
  flex: 1;
  padding: ${spacings.xs};
  min-height: 200px;
  min-width: 120px;
  overflow: hidden;
  color: ${colors.carbon800};
  background-color: white;
  box-shadow: 0 2px ${spacings.xxs} rgba(0, 0, 0, 0.1);
  position: relative;
`;

const matrixCellLastColumn = css`
  margin-right: 0;
`;

const matrixCellNotLastColumn = css`
  margin-right: ${MATRIX_UI_CONSTANTS.MATRIX_GAP};
`;

const matrixCellWithData = css`
  overflow-y: auto;
  overflow-x: visible;
`;

const matrixCellBorderLastCorner = css`
  border-bottom-right-radius: ${MATRIX_UI_CONSTANTS.HEADER_BORDER_RADIUS};
`;

// Monetary badge styles
const monetaryBadgeBase = css`
  position: absolute;
  top: 0px;
  right: 0px;
  width: 66px;
  text-align: center;
  font-size: 1em;
  font-weight: bold;
  padding: 6px ${spacings.xxs};
  display: inline-block;
  z-index: 1;
  box-sizing: border-box;
`;

const monetaryBadgeText = css`
  font-size: 14px
  display: block;
  opacity: 1;
  cursor: help;
`;

const monetaryBadgeWithData = css`
  color: ${colors.carbon900};
`;

const monetaryBadgeNoData = css`
  color: ${colors.carbon500};
  background-color: ${colors.titanium100};
`;

const minorFaultsToggle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${spacings.xs} 0;
  padding: ${spacings.xxs} ${spacings.xs};
  background-color: ${colors.titanium50};
  border: 1px solid #dee2e6;
  border-radius: ${spacings.xxs};
  font-size: 11px;
  color: #6c757d;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e9ecef;
  }
`;

const minorFaultsContainer = css`
  background-color: ${colors.titanium50};
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 ${spacings.xxs} ${spacings.xxs};
  margin: 0 0 ${spacings.xs} 0;
  padding: ${spacings.xs};
`;

const minorFaultsList = css`
  margin: 0;
  padding: 0;
  list-style: none;
`;

interface PlantItemProps {
  plant: string;
  plantEntry: MatrixEntry | undefined;
  repairIndex: number;
  hasUnknownReparability?: boolean;
}

const plantTagRecurrent = css`
  &.light {
    background-color: ${colors.phosphorus100};
  }
`;

const plantTagUnknownReparability = css`
  &.light {
    background-color: #fff3cd;
  }
`;

const PlantItem: React.FC<PlantItemProps> = ({
  plant,
  plantEntry,
  repairIndex,
  hasUnknownReparability = false,
}) => {
  const isRecurrentPlant = Boolean(repairIndex === 0 && plantEntry && plantEntry.isRecurrentFault);
  const isUnknownReparability = hasUnknownReparability && repairIndex === 1; // ATF column

  const plantTooltip = plantEntry
    ? formatPlantTooltip(plantEntry.plant || plant, plantEntry.impact)
    : plant;

  const getTagClassName = () => {
    if (isRecurrentPlant) {
      return plantTagRecurrent;
    }
    if (isUnknownReparability) {
      return plantTagUnknownReparability;
    }
    return "";
  };

  return (
    <span key={plant} className={plantItem}>
      <Tag size="s" theme="light" title={plantTooltip} className={getTagClassName()}>
        {isRecurrentPlant && <RepeatLine size="s" />}
        {isUnknownReparability && <QuestionMark size="s" />}
        <Ellipsis>
          {plant}
          {plantEntry && (
            <span className={impactCostText}>{formatImpactDisplay(plantEntry.impact)}</span>
          )}
        </Ellipsis>
      </Tag>
    </span>
  );
};

interface FailureItemProps {
  item: DisplayItem;
  repairIndex: number;
  unknownReparabilityPlants?: Set<string>;
}

const impactBadgeStyle = css`
  &.light {
    background-color: ${colors.darkNitrogen900};
    color: white;
  }
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;

const FailureItem: React.FC<FailureItemProps> = ({
  item,
  repairIndex,
  unknownReparabilityPlants,
}) => {
  const reparabilityCategory = getReparabilityCategory(repairIndex);
  const sortedPlants = processPlants(item.plants, item.entries);

  return (
    <li className={failureItem}>
      <div className={failureHeader}>
        <span className={failureLabel} title={item.label}>
          {item.label}
        </span>
        <Tag
          size="s"
          theme="light"
          title={t("reporting.repairability.matrix.fault_impact")}
          className={impactBadgeStyle}
        >
          <span style={{ fontSize: "${spacings.s}" }}>
            {Math.abs(item.totalImpact) < 1 ? "<1" : item.totalImpact.toFixed(0)} MW |{" "}
            {formatMonetaryValue(calculateCost(item.totalImpact, reparabilityCategory))}
          </span>
        </Tag>
      </div>
      <div className={plantsContainer}>
        {sortedPlants.map(({ plant, plantEntry }) => (
          <PlantItem
            key={plant}
            plant={plant}
            plantEntry={plantEntry}
            repairIndex={repairIndex}
            hasUnknownReparability={unknownReparabilityPlants?.has(plant)}
          />
        ))}
      </div>
    </li>
  );
};

interface MatrixCellProps {
  category: string;
  range: ImpactRange;
  repairIndex: number;
  impactIndex: number;
  cellData: MatrixEntry[];
  selectedPlants: string[];
  isLastColumn: boolean;
  isLastRow: boolean;
}

const MatrixCell: React.FC<MatrixCellProps> = ({
  category,
  range,
  repairIndex,
  impactIndex,
  cellData,
  selectedPlants,
  isLastColumn,
  isLastRow,
}) => {
  const [showMinorFaults, setShowMinorFaults] = useState(false);
  const borderColor = getBorderColor(repairIndex, impactIndex);
  const reparabilityCategory = getReparabilityCategory(repairIndex);

  const displayResult = getDisplayItems(cellData, reparabilityCategory);
  const displayItems = displayResult.items;
  const unknownReparabilityPlants = displayResult.unknownReparabilityPlants;
  const hiddenItems = displayResult.hiddenItems;
  const hasHiddenFaults = displayResult.hasHiddenFaults;
  const hasData = displayItems.length > 0 || hiddenItems.length > 0;

  const handleToggleMinorFaults = () => {
    setShowMinorFaults(prev => !prev);
  };

  const totalImpactInCell = cellData.reduce((sum, entry) => sum + Math.abs(entry.impact), 0);
  const estimatedCost = calculateCost(totalImpactInCell, reparabilityCategory);

  const actualBorderColor = hasData ? borderColor : colors.titanium700;

  const matrixCellClasses = getMatrixCellClasses(hasData, isLastColumn, isLastRow);

  const cellStyle = {
    borderLeft: `3px solid ${actualBorderColor}`,
    borderTop: `7px solid ${actualBorderColor}`,
    borderRight: `3px solid ${actualBorderColor}`,
    borderBottom: `3px solid ${actualBorderColor}`,
  };

  const monetaryBadgeClasses = [
    monetaryBadgeBase,
    hasData ? monetaryBadgeWithData : monetaryBadgeNoData,
  ].join(" ");

  const monetaryBadgeBackgroundStyle = hasData
    ? {
        backgroundColor: getLighterBackgroundColor(borderColor),
      }
    : {};

  return (
    <div
      key={`${range.label}-${category}-${hasData ? "data" : "no-data"}-${selectedPlants.join("-")}`}
      className={matrixCellClasses}
      style={cellStyle}
    >
      <div className={monetaryBadgeClasses} style={monetaryBadgeBackgroundStyle}>
        <span
          className={monetaryBadgeText}
          title={t("reporting.repairability.matrix.cell_impact.cost")}
        >
          {formatMonetaryValue(estimatedCost)}
        </span>
      </div>

      {hasData && (
        <ul className={failureList}>
          {displayItems.map(item => (
            <FailureItem
              key={`${item.label}-${item.count}`}
              item={item}
              repairIndex={repairIndex}
              unknownReparabilityPlants={unknownReparabilityPlants}
            />
          ))}

          {/* Toggle section for hidden faults */}
          {hasHiddenFaults && (
            <li className={failureItem}>
              <div
                className={minorFaultsToggle}
                onClick={handleToggleMinorFaults}
                title={
                  showMinorFaults
                    ? t("reporting.repairability.matrix.minor_faults.hide")
                    : t("reporting.repairability.matrix.minor_faults.show")
                }
                style={{
                  borderRadius: showMinorFaults
                    ? "${spacings.xxs} ${spacings.xxs} 0 0"
                    : "${spacings.xxs}",
                  marginBottom: showMinorFaults ? 0 : "${spacings.xs}",
                }}
              >
                {showMinorFaults
                  ? t("reporting.repairability.matrix.minor_faults.hide_text", {
                      count: hiddenItems.length,
                      icon: "▼",
                    })
                  : t("reporting.repairability.matrix.minor_faults.show_text", {
                      count: hiddenItems.length,
                      icon: "►",
                    })}
              </div>

              {/* Show hidden faults when toggled on within the container */}
              {showMinorFaults && (
                <div className={minorFaultsContainer}>
                  <ul className={minorFaultsList}>
                    {hiddenItems.map(item => (
                      <FailureItem
                        key={`hidden-${item.label}-${item.count}`}
                        item={item}
                        repairIndex={repairIndex}
                        unknownReparabilityPlants={unknownReparabilityPlants}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </li>
          )}
        </ul>
      )}

      {!hasData && (
        <div className={emptyCellMessage}>{t("reporting.repairability.matrix.empty_cell")}</div>
      )}
    </div>
  );
};

const groupItemsByFailure = (cellData: MatrixEntry[]) => {
  const groupedItems = new Map();
  const unknownReparabilityPlants = new Set<string>();

  for (const item of cellData) {
    const groupName = getFailureGroupName(item.failure);
    const key = groupName;
    const groupDisplayName = groupName;

    if (item.hasUnknownReparability) {
      unknownReparabilityPlants.add(item.trigram);
    }

    if (groupedItems.has(key)) {
      const existing = groupedItems.get(key);
      existing.count += 1;
      existing.plants.add(item.trigram);
      existing.totalImpact += item.impact;
      existing.entries.push(item);
      existing.hasRecurrentFaults = existing.hasRecurrentFaults || item.isRecurrentFault;
    } else {
      groupedItems.set(key, {
        label: groupDisplayName,
        count: 1,
        plants: new Set([item.trigram]),
        totalImpact: item.impact,
        entries: [item],
        hasRecurrentFaults: item.isRecurrentFault,
      });
    }
  }

  return { groupedItems, unknownReparabilityPlants };
};

const processDisplayItems = (allItems: DisplayItem[]) => {
  const shouldFilterMinorFaults =
    allItems.length > MATRIX_UI_CONSTANTS.MAX_VISIBLE_FAULTS_THRESHOLD;

  if (shouldFilterMinorFaults) {
    const majorItems = allItems.filter(item => Math.abs(item.totalImpact) >= 0.5);
    const minorItems = allItems.filter(item => Math.abs(item.totalImpact) < 0.5);

    return {
      items: majorItems,
      hiddenItems: minorItems,
      hasHiddenFaults: minorItems.length > 0,
    };
  }

  return {
    items: allItems,
    hiddenItems: [],
    hasHiddenFaults: false,
  };
};

const getDisplayItems = (
  cellData: MatrixEntry[],
  reparabilityCategory?: Reparability,
): {
  items: DisplayItem[];
  unknownReparabilityPlants: Set<string>;
  hiddenItems: DisplayItem[];
  hasHiddenFaults: boolean;
} => {
  if (!cellData || cellData.length === 0)
    return {
      items: [],
      unknownReparabilityPlants: new Set(),
      hiddenItems: [],
      hasHiddenFaults: false,
    };

  const { groupedItems, unknownReparabilityPlants } = groupItemsByFailure(cellData);

  const allItems = Array.from(groupedItems.values()).map(item => ({
    ...item,
    plants: Array.from(item.plants),
    totalCost: reparabilityCategory ? calculateCost(item.totalImpact, reparabilityCategory) : 0,
  }));

  // Sort items by impact
  allItems.sort((a, b) => b.totalImpact - a.totalImpact);

  const { items, hiddenItems, hasHiddenFaults } = processDisplayItems(allItems);

  return {
    items,
    unknownReparabilityPlants,
    hiddenItems,
    hasHiddenFaults,
  };
};

interface ReparabilityImpactMatrixInternalProps {
  reporting: MatrixReporting;
  translation: Translation;
  selectedYear: number;
  selectedMonth: number;
  selectedPlants: string[];
}

export const ReparabilityImpactMatrixInternal: React.FC<ReparabilityImpactMatrixInternalProps> = ({
  reporting,
  translation,
  selectedYear,
  selectedMonth,
  selectedPlants,
}) => {
  const translations = useTranslations();
  const reparabilityDescriptions = useReparabilityDescriptions();

  const matrixData = useMemo(() => {
    const filters: MatrixFilters = {
      selectedYear,
      selectedMonth,
      selectedPlants,
      displayMode: "failure",
      useColorfulPlants: false,
    };

    const result = buildReparabilityMatrix(
      reporting as MatrixReporting,
      filters,
      translation,
      (_key: string, value: ReparabilityKey) => t("reparability", value, translations),
      buildMatrixEntry,
    );

    return {
      matrix: result.matrix,
      reparabilityCategories: result.reparabilityCategories,
      impactRanges: result.impactRanges,
    };
  }, [reporting, translation, selectedYear, selectedMonth, selectedPlants, translations]);

  return (
    <div className={matrixContainer}>
      <div className={recurringLegendContainer}>
        <span className={recurringLegendLabel}>
          {t("reporting.repairability.matrix.legend.recurring_faults")}
        </span>
        <Tag size="s" theme="light" className={legendBadgeRecurrent}>
          <RepeatLine size="s" />
          <span style={{ marginLeft: "${spacings.xxs}" }}>SITE</span>
        </Tag>
      </div>
      <div className={recurringLegendContainer}>
        <span className={recurringLegendLabel}>
          {t("reporting.repairability.matrix.legend.unknown_repairability", {
            atf: reparabilityDescriptions[1] || "",
          })}
        </span>
        <Tag size="s" theme="light" className={legendBadgeUnknownReparability}>
          <QuestionMark size="s" />
          <span style={{ marginLeft: "${spacings.xxs}" }}>SITE</span>
        </Tag>
      </div>
      <div className={recurringLegendContainer}>
        <span className={recurringLegendLabel}>
          {t("reporting.repairability.matrix.legend.cost")}
        </span>
        <Tag size="s" theme="light" className={legendBadgeCost}>
          <span style={{ fontSize: "${spacings.s}" }}>
            {t("reporting.repairability.matrix.legend.badge")}
          </span>
        </Tag>
      </div>
      <div className={totalFinancialImpactRow}>
        <span className={totalFinancialImpactValue}>
          {t("reporting.repairability.matrix.total_financial_impact")}{" "}
          {formatMonetaryValue(calculateTotalFinancialImpact(matrixData))}
        </span>
      </div>
      <div className={headerRow}>
        <div className={headerSpacer} />
        {matrixData.reparabilityCategories.map((category, index) => {
          const financialImpact = calculateColumnFinancialImpact(matrixData, index);
          return (
            <div key={category} className={headerCell}>
              <span className={headerCellTitle}>{reparabilityDescriptions[index] || ""}</span>
              <span className={headerCellFinancialImpact}>
                {t("reporting.repairability.matrix.total")} {formatMonetaryValue(financialImpact)}
              </span>
            </div>
          );
        })}
      </div>
      {matrixData.impactRanges
        .map((range, impactIndex) => (
          <div key={range.label} className={matrixRow}>
            <div className={`${yAxisLabel}`}>{range.label}</div>

            {matrixData.reparabilityCategories.map((category, repairIndex) => (
              <MatrixCell
                key={`${range.label}-${category}`}
                category={category}
                range={range}
                repairIndex={repairIndex}
                impactIndex={impactIndex}
                cellData={matrixData.matrix[category]?.[range.label] || []}
                selectedPlants={selectedPlants}
                isLastColumn={repairIndex === matrixData.reparabilityCategories.length - 1}
                isLastRow={impactIndex === 0}
              />
            ))}
          </div>
        ))
        .reverse()}
    </div>
  );
};

interface ReparabilityImpactMatrixCardProps {
  selectedYear: number;
  selectedMonth: number;
  selectedPlants: string[];
}

export function ReparabilityImpactMatrixCard({
  selectedYear,
  selectedMonth,
  selectedPlants,
}: ReparabilityImpactMatrixCardProps) {
  const reporting = useDiagnosisReporting();
  const translation = useTranslations();

  return (
    <div className="reparability-impact-matrix">
      <ReparabilityImpactMatrixInternal
        reporting={reporting as MatrixReporting}
        translation={translation}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        selectedPlants={selectedPlants}
      />
    </div>
  );
}
